import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import { School, Assignment, EventAvailable } from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import StatsCard from "../../components/data-display/StatsCard";
import advancedReportService from "../../services/advancedReportService";
import {
  REPORT_GRANULARITY,
  REPORT_PERIOD_OPTIONS,
  buildAttendanceDoughnutChart,
  buildComparisonBarChart,
} from "../../utils/advancedReportCharts";
import { formatPercent } from "../../utils/formatPercent";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const TeacherReports = () => {
  const { user } = useSelector((state) => state.auth);
  const { selectedInstitute } = useSelector((state) => state.instituteContext);

  const effectiveInstituteId = useMemo(() => {
    const fromUser = user?.instituteId;
    if (fromUser != null && fromUser !== "") return Number(fromUser);
    if (selectedInstitute?.id) return selectedInstitute.id;
    return null;
  }, [user?.instituteId, selectedInstitute?.id]);

  const [snapshotPeriod, setSnapshotPeriod] = useState("monthly");
  const [instituteSnapshot, setInstituteSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  useEffect(() => {
    if (!effectiveInstituteId) {
      setInstituteSnapshot(null);
      setSnapshotLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setSnapshotLoading(true);
      try {
        const res = await advancedReportService.getDepartmentReport({
          period: snapshotPeriod,
          instituteId: effectiveInstituteId,
        });
        if (!cancelled) setInstituteSnapshot(res?.data?.data ?? null);
      } catch (e) {
        console.warn("Student analytics snapshot failed", e);
        if (!cancelled) setInstituteSnapshot(null);
      } finally {
        if (!cancelled) setSnapshotLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveInstituteId, snapshotPeriod]);

  const snapshotDoughnut = useMemo(
    () =>
      buildAttendanceDoughnutChart({
        granularity: REPORT_GRANULARITY.DEPARTMENT,
        data: instituteSnapshot,
        selectedStudent: null,
      }),
    [instituteSnapshot]
  );

  const snapshotBar = useMemo(
    () =>
      buildComparisonBarChart({
        granularity: REPORT_GRANULARITY.DEPARTMENT,
        data: instituteSnapshot,
      }),
    [instituteSnapshot]
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Reports & Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Institute-wide student metrics. Use advanced reports for department → class → student drill-down.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Student analytics snapshot
        </Typography>
        {!effectiveInstituteId ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Associate an institute with your account to load institute-wide attendance and assignment metrics.
          </Alert>
        ) : (
          <>
            {snapshotLoading && <LinearProgress sx={{ mb: 2 }} />}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Period</InputLabel>
                    <Select
                      label="Period"
                      value={snapshotPeriod}
                      onChange={(e) => setSnapshotPeriod(e.target.value)}
                    >
                      {REPORT_PERIOD_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button component={RouterLink} to="/analytics/reports" size="small" variant="outlined">
                    Advanced drill-down reports
                  </Button>
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <StatsCard
                      value={instituteSnapshot?.summary?.totalDepartments ?? 0}
                      label="Departments"
                      icon={<School />}
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatsCard
                      value={formatPercent(instituteSnapshot?.summary?.avgAttendance ?? 0)}
                      label="Avg attendance"
                      icon={<EventAvailable />}
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatsCard
                      value={formatPercent(instituteSnapshot?.summary?.avgAssignmentCompletion ?? 0)}
                      label="Avg assignment completion"
                      icon={<Assignment />}
                      color="info"
                    />
                  </Grid>
                </Grid>
                {instituteSnapshot && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ height: 240, display: "flex", justifyContent: "center" }}>
                        <Doughnut
                          data={snapshotDoughnut}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ height: 240 }}>
                        <Bar
                          data={snapshotBar}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "top" } },
                            scales: {
                              y: {
                                beginAtZero: true,
                                max: 100,
                                ticks: { callback: (v) => `${v}%` },
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TeacherReports;
